import React from "react";
import styled from "styled-components";
import {
  getPriceByBillingType,
  PriceTermSettings,
  ProductAttributeInputModel,
  RecurrencePriceSettings,
} from "../models";
import { ItemBillingType } from "../enums";
import {
  CustomLabel,
  FlexContainer,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { AccountSettingsStore } from "../store";
import { observer } from "mobx-react";

type IProps = {
  terms: PriceTermSettings;
  recurrence: RecurrencePriceSettings;
  billingType: keyof typeof ItemBillingType;
  itemType: string;
  hasVariants: boolean;
  variants: ProductAttributeInputModel;
  variantIndex?: number; //selected variant index
  quantity: number;
  className?: string;
  priceId: string;
};

export const AmountRow = styled(FlexContainer)`
  flex-wrap: wrap;
  margin-bottom: 35px;
  &.price-centered-row {
    justify-content: center;
    flex-direction: column;
    /* align-items: center; */ // creating issue for the single item step UI
    &.price-centered-row-multiple {
      align-items: center;
      height: 48px;
      flex: unset;
      margin-bottom: 0px !important;
      .major-price {
        display: block !important;
      }
    }
  }
  &.price-in-single-row {
    justify-content: center;
    flex-direction: row;
  }

  ${CustomLabel} {
    font-weight: 600;
    color: #000;
    font-size: 16px;
    text-align: center;
    span {
      font-weight: 400;
    }
  }
  .term-recurring {
    text-align: center;
    white-space: nowrap;
  }
  &.recommend-price-desc {
    margin-top: 15px;
    align-items: center;
    span {
      font-size: 16px;
    }
  }
  &.price-add-on {
    align-items: center;
    margin-bottom: 35px;
    ${CustomLabel} {
      font-weight: 600;
      font-size: 18px;
      span {
        font-weight: 400;
      }
    }
    &.price-addon-wo-desc {
      margin-top: 15px;
    }
    &.add-on-no-margin {
      margin-bottom: 5px;
    }
  }
  &.price-with-warning {
    margin-bottom: 0px;
  }

  .major-price {
    font-size: 20px;
    font-weight: 600;
    color: #000;
    line-height: 24px;
    padding-right: 5px;
    display: inline-block;
  }
  @media (min-width: 300px) and (max-width: 768px) {
    ${CustomLabel} {
      font-weight: 600;
      color: #000;
      font-size: 18px;
      text-align: center;
      line-height: 20px;
      span {
        font-weight: 400;
      }
    }
    .major-sub-price {
      font-size: 16px;
    }
    &.price-add-on {
      ${CustomLabel} {
        font-size: 16px;
      }
    }
  }
`;

const PurchasePriceDescriptionRow: React.FC<IProps> = ({
  terms,
  recurrence,
  billingType,
  itemType,
  hasVariants,
  variants,
  variantIndex,
  quantity,
  className,
  priceId,
}) => {
  const priceDescription = getPriceByBillingType(
    itemType,
    hasVariants,
    variants,
    priceId,
    billingType,
    terms,
    recurrence,
    quantity,
    variantIndex
  );

  // const hideBillingFrequencey = itemType === 'Product';

  if (!priceDescription) return null;
  switch (billingType) {
    case "Recurring":
      const recurringPrice =
        (recurrence?.standardPricing?.price || 0) * quantity;
      return (
        <AmountRow className={className}>
          <div className="term-recurring">
            <span className="major-price">
              &nbsp;
              {"$" +
                AccountSettingsStore.formatAmount(
                  priceDescription.subTotal
                )}{" "}
              {/* <span className="charcoal-label">USD / EVERY</span> {recurrence.billingIntervalCount}{' '}
                      <span className="charcoal-label">{recurrence.billingInterval}</span> */}
            </span>
            {(recurringPrice && recurringPrice > 0 && (
              <CustomLabel className="major-sub-price">
                {" "}
                + $
                {AccountSettingsStore.formatAmount(
                  parseFloat(recurringPrice?.toString() || "0"),
                  true
                )}{" "}
                {/* <span className="charcoal-label"> SETUP </span> +{' '} */}
                <span className="charcoal-label">every</span>{" "}
                {recurrence.billingIntervalCount}{" "}
                <span className="charcoal-label">
                  {recurrence.billingInterval?.toLocaleLowerCase()}
                  {recurrence.billingIntervalCount > 1 && "s"}
                </span>
              </CustomLabel>
            )) ||
              null}
          </div>
        </AmountRow>
      );
    case "Term":
      return (
        <AmountRow className={className}>
          <CustomLabel className="major-price">
            {"$" + AccountSettingsStore.formatAmount(priceDescription.subTotal)}{" "}
          </CustomLabel>
        </AmountRow>
      );

    case "TermPlusRecurring":
      return (
        <AmountRow className={className}>
          <div className="term-recurring">
            {priceDescription.price && (
              <span className="major-price">
                {"$" +
                  AccountSettingsStore.formatAmount(priceDescription.subTotal)}
              </span>
            )}
            <CustomLabel fontSize="14px">
              {" "}
              + $
              {AccountSettingsStore.formatAmount(
                priceDescription.termPlusRecurringRenewalPrice
              )}{" "}
              <span className="charcoal-label">every </span>
            </CustomLabel>
            <CustomLabel fontSize="14px">
              {" "}
              &nbsp;{recurrence.billingIntervalCount}
            </CustomLabel>
            <span className="charcoal-label">
              {" "}
              {recurrence.billingInterval?.toLocaleLowerCase()}
              {recurrence.billingIntervalCount > 1 && "s"}
            </span>
          </div>
        </AmountRow>
      );
    default:
      return null;
  }
};

export default observer(PurchasePriceDescriptionRow);
